import type { EnrichmentDictionaryEntry } from './getEnrichmentDictionary'
import getEnrichmentDictionary from './getEnrichmentDictionary'

type EnrichmentWithMatchIndex = EnrichmentDictionaryEntry & {
  index: number
}

/**
 * Given a bag of words, find keyword matches and return a list of labelled links.
 * Finds the first, exclusive match on ` ${token}`, with the longest tokens first.
 *
 * @param text Bag of words to surface enriched text
 */
export default function getEnrichedText(text: string): {
  enrichments: EnrichmentWithMatchIndex[]
  text: string
} {
  if (!text.trim()) {
    return { enrichments: [], text }
  }

  const matchers = getSortedMatchers() // match longest tokens first
  const { enrichments } = matchers.reduce(findMatchReducer, {
    // add initial spaces to catch single word attempts
    enrichments: [],
    text: ` ${text}`,
  })

  return {
    enrichments,
    text,
  }
}

function getSortedMatchers(): EnrichmentDictionaryEntry[] {
  return Array.from(getEnrichmentDictionary().values()).sort(
    (a, b) => b.keyword.length - a.keyword.length,
  )
}

// For each iteration, XXX out matched text so they aren't matched again
function findMatchReducer(
  {
    enrichments,
    text,
  }: { enrichments: EnrichmentWithMatchIndex[]; text: string },
  enrichmentCategory: EnrichmentDictionaryEntry,
): {
  enrichments: EnrichmentWithMatchIndex[]
  text: string
} {
  const matches = enrichmentCategory.matcher.exec(text)
  if (!matches) {
    return { enrichments, text }
  }

  enrichments.push({ ...enrichmentCategory, index: matches.index })
  // replace match with 'X's so they aren't found again
  const replacedText = text.replace(
    enrichmentCategory.matcher,
    ` ${enrichmentCategory.keyword
      .split('')
      .reduce((prev, _) => `${prev}X`, '')} `,
  )
  return { enrichments, text: replacedText }
}
